import {
  Product,
  Collection,
  Settings,
  Order,
  CartItem,
  FilterState,
  CreateOrderPayload,
  BalanceInfo,
} from '../types';
import { useUserStore } from '../store/userStore';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

function getInitData(): string {
  try {
    // @ts-ignore
    return window.Telegram?.WebApp?.initData || '';
  } catch {
    return '';
  }
}

async function apiFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const initData = getInitData();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string>),
  };

  if (initData) {
    headers['Authorization'] = `tma ${initData}`;
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorMsg = `API error: ${response.status}`;
    let code: string | undefined;
    try {
      const errData = await response.json();
      if (errData.error) errorMsg = errData.error;
      if (errData.code) code = errData.code;
    } catch { /* ignore parse error */ }
    const err = new Error(errorMsg) as Error & { code?: string };
    if (code) err.code = code;
    if (code === 'USER_BLOCKED') {
      useUserStore.getState().setBlocked(true);
    }
    throw err;
  }

  return response.json();
}

export interface MeResponse {
  id: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  selectedRegion?: string;
  blocked: boolean;
}

export const api = {
  // Products
  async getProducts(filters?: Partial<FilterState> & { limit?: number; offset?: number; category?: string }): Promise<{ products: Product[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.category) params.set('category', filters.category);
    else if (filters?.categories?.length) params.set('category', filters.categories[0]);
    if (filters?.platforms?.length) params.set('platform', filters.platforms[0]);
    if (filters?.regions?.length) params.set('region', filters.regions[0]);
    if (filters?.priceRange) {
      params.set('minPrice', String(filters.priceRange[0]));
      params.set('maxPrice', String(filters.priceRange[1]));
    }
    if (filters?.limit) params.set('limit', String(filters.limit));
    if (filters?.offset) params.set('offset', String(filters.offset));
    return apiFetch<{ products: Product[]; total: number }>(`/api/products?${params.toString()}`);
  },

  async getDonatsBySubtitle(subtitle: string, limit?: number, offset?: number): Promise<{ products: Product[]; total: number }> {
    const params = new URLSearchParams();
    params.set('category', 'donat');
    params.set('subtitle', subtitle);
    if (limit) params.set('limit', String(limit));
    if (offset) params.set('offset', String(offset));
    return apiFetch<{ products: Product[]; total: number }>(`/api/products?${params.toString()}`);
  },

  async searchProducts(query: string, category?: string, region?: string): Promise<Product[]> {
    const params = new URLSearchParams();
    params.set('search', query);
    if (category && category !== 'all') params.set('category', category);
    if (region) params.set('region', region);
    const res = await apiFetch<{ products: Product[]; total: number } | Product[]>(`/api/products?${params.toString()}`);
    return Array.isArray(res) ? res : res.products;
  },

  async getProduct(id: string): Promise<Product | null> {
    try {
      return await apiFetch<Product>(`/api/products/${id}`);
    } catch {
      return null;
    }
  },

  // Collections
  async getCollections(section: 'store' | 'sharing' = 'store'): Promise<Collection[]> {
    return apiFetch<Collection[]>(section === 'sharing' ? '/api/collections?section=sharing' : '/api/collections');
  },

  async getCollection(
    id: string,
    opts?: { limit?: number; offset?: number },
  ): Promise<{ id: string; title: string; total: number; products: Product[] } | null> {
    try {
      const limit = opts?.limit ?? 100;
      const offset = opts?.offset ?? 0;
      return await apiFetch<{ id: string; title: string; total: number; products: Product[] }>(
        `/api/collections/${id}?limit=${limit}&offset=${offset}`,
      );
    } catch {
      return null;
    }
  },

  // Settings
  async getSettings(): Promise<Settings> {
    return apiFetch<Settings>('/api/settings');
  },

  // Me (current user; 403 with code USER_BLOCKED if blocked)
  async getMe(): Promise<MeResponse> {
    return apiFetch<MeResponse>('/api/me');
  },

  // Внутренний баланс + реферальная сводка
  async getBalance(): Promise<BalanceInfo> {
    return apiFetch<BalanceInfo>('/api/me/balance');
  },

  // Orders
  async createOrder(payload: CreateOrderPayload): Promise<Order> {
    return apiFetch<Order>('/api/orders', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  // Пополнение Steam: клиент платит payAmount (с наценкой), на Steam зачисляется amount.
  async steamTopup(steamLogin: string, amount: number): Promise<Order & { paymentUrl?: string | null; payAmount?: number; creditAmount?: number }> {
    return apiFetch('/api/orders/steam-topup', {
      method: 'POST',
      body: JSON.stringify({ steamLogin, amount }),
    });
  },

  // Коды пополнения (AppRoute): витрина сервисов и заказ
  async getCodeServices(): Promise<{
    enabled: boolean;
    services: Array<{
      key: string;
      title: string;
      denominations: Array<{ id: string; name: string; priceRub: number; countryCode?: string; group?: string }>;
    }>;
  }> {
    return apiFetch('/api/codes/services');
  },

  async createCodeOrder(denominationId: string, quantity: number, paymentMethod?: 'SBP' | 'CRYPTO'): Promise<Order & { paymentUrl?: string | null; stubPayAvailable?: boolean; useBotFlow?: boolean }> {
    return apiFetch('/api/codes/orders', {
      method: 'POST',
      body: JSON.stringify({ denominationId, quantity, paymentMethod }),
    });
  },

  async getOrder(id: string): Promise<Order | null> {
    try {
      return await apiFetch<Order>(`/api/orders/${id}`);
    } catch {
      return null;
    }
  },

  async stubPayOrder(orderId: string): Promise<{ ok: boolean; alreadyPaid?: boolean }> {
    return apiFetch(`/api/orders/${orderId}/stub-pay`, { method: 'POST' });
  },

  async getOrders(): Promise<Order[]> {
    try {
      return await apiFetch<Order[]>('/api/orders');
    } catch {
      return [];
    }
  },

  // Favorites
  async getFavorites(): Promise<Product[]> {
    return apiFetch<Product[]>('/api/favorites');
  },

  async addFavorite(productId: string): Promise<void> {
    await apiFetch(`/api/favorites/${productId}`, { method: 'POST' });
  },

  async removeFavorite(productId: string): Promise<void> {
    await apiFetch(`/api/favorites/${productId}`, { method: 'DELETE' });
  },

  // Cart
  async getCart(): Promise<CartItem[]> {
    return apiFetch<CartItem[]>('/api/cart');
  },

  async addToCart(productId: string, variantId: string, quantity = 1): Promise<void> {
    await apiFetch('/api/cart', {
      method: 'POST',
      body: JSON.stringify({ productId, variantId, quantity }),
    });
  },

  async removeFromCart(id: string): Promise<void> {
    await apiFetch(`/api/cart/${id}`, { method: 'DELETE' });
  },

  // Promo
  async validatePromo(code: string, userId?: string): Promise<{ valid: boolean; discount?: number; error?: string }> {
    return apiFetch('/api/settings/promo/validate', {
      method: 'POST',
      body: JSON.stringify({ code, userId }),
    });
  },

  // Banners
  async getBanners(section: 'store' | 'sharing' = 'store'): Promise<Array<{ id: string; title: string; imageUrl: string; linkUrl: string | null; linkType: string; position: number }>> {
    return apiFetch(section === 'sharing' ? '/api/banners?section=sharing' : '/api/banners');
  },

};
